import * as DialogPrimitive from '@radix-ui/react-dialog';
import { CircleAlert, X } from 'lucide-react';
import { useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { PromptTemplateDropdown } from '@/Domain/components/KnowledgeBase/PromptTemplateDropdown';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { FieldError } from '@/Domain/components/Shared/Field/Field';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';

interface MainPromptSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  control: Control<KnowledgeBase>;
  onApplyTemplate: (text: string) => void;
  hasValue: boolean;
  maxLength: number;
}

export const MainPromptSheet = ({
  open,
  onOpenChange,
  control,
  onApplyTemplate,
  hasValue,
  maxLength,
}: MainPromptSheetProps) => {
  const { t, i18n } = useTranslation(['knowledge_base', 'validation', 'common']);
  const [showEmptyWarning, setShowEmptyWarning] = useState(false);

  // The AI Agent can't run without a Main Prompt, so block closing while it's empty.
  const requestClose = () => {
    if (!hasValue) {
      setShowEmptyWarning(true);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}>
      <SheetContent
        className="flex w-full flex-col sm:max-w-2xl"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          requestClose();
        }}
      >
        <SheetHeader className="flex-row items-start">
          <div className="min-w-0 flex-1">
            <SheetTitle>{t('knowledge_base:form.description.label')}</SheetTitle>
            <SheetDescription>{t('knowledge_base:form.description.help')}</SheetDescription>
          </div>
          <IconButton variant="secondary" className="shrink-0" onClick={requestClose}>
            <X className="size-4" />
          </IconButton>
        </SheetHeader>

        <div className="mt-2 flex min-h-0 flex-1 flex-col">
          <Controller
            name="short_description"
            control={control}
            rules={{
              // No maxLength rule: useAutoSubmit strips invalid fields from the PATCH.
              required: t('required', { ns: 'validation' }),
              validate: (v) => (v ?? '').trim().length > 0 || t('required', { ns: 'validation' }),
            }}
            render={({ field, fieldState: { error } }) => {
              const empty = !(field.value ?? '').trim();
              return (
                <div className="relative flex min-h-0 flex-1 flex-col">
                  <div className="relative min-h-0 flex-1">
                    <AutosizeTextarea
                      {...field}
                      value={field.value ?? ''}
                      error={empty}
                      className="!h-full rounded-xl px-4 pb-12 pt-3"
                      maxLength={maxLength}
                      data-testid="identity-main-prompt-textarea"
                    />
                    <PromptTemplateDropdown
                      hasExistingValue={hasValue}
                      onApply={onApplyTemplate}
                      triggerClassName="absolute bottom-2 left-2"
                      dropdownAlign="start"
                    />
                    <span
                      className="absolute bottom-2 right-3 text-xs text-muted-foreground"
                      data-testid="identity-main-prompt-counter"
                    >
                      {(field.value ?? '').length.toLocaleString(i18n.language)} /{' '}
                      {maxLength.toLocaleString(i18n.language)} {t('common:characters')}
                    </span>
                  </div>
                  <FieldError className="mt-1.5">
                    {empty ? t('required', { ns: 'validation' }) : error?.message}
                  </FieldError>
                </div>
              );
            }}
          />
        </div>

        <DialogPrimitive.Root open={showEmptyWarning} onOpenChange={setShowEmptyWarning}>
          <DialogPrimitive.Overlay className="absolute inset-0 z-20 bg-black/30" />
          <DialogPrimitive.Content
            role="alertdialog"
            aria-modal="true"
            className="absolute inset-0 z-30 flex items-center justify-center p-4 focus:outline-none"
          >
            <div className="w-full max-w-md space-y-3 rounded-lg border border-border bg-card p-6 shadow-lg">
              <div className="flex items-center gap-2">
                <CircleAlert className="size-5 text-warning" />
                <DialogPrimitive.Title className="text-base font-semibold text-foreground">
                  {t('knowledge_base:form.description.required_title')}
                </DialogPrimitive.Title>
              </div>
              <DialogPrimitive.Description className="text-sm text-muted-foreground">
                {t('knowledge_base:form.description.required_message')}
              </DialogPrimitive.Description>
              <div className="flex justify-end pt-2">
                <Button size="sm" onClick={() => setShowEmptyWarning(false)}>
                  {t('knowledge_base:form.description.required_action')}
                </Button>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
